import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, X } from 'lucide-react';
import { useLang } from '@/contexts/LanguageContext';

export default function CookieConsent() {
  const { t, dir } = useLang();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState({
    necessary: true,
    analytics: true,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('tw_cookie_consent');
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Global hook: any element can dispatch `window.dispatchEvent(new Event('tw:open-cookies'))`
  // (e.g. the footer "Cookie Settings" link) to re-open the banner.
  useEffect(() => {
    const open = () => { setShowDetails(true); setVisible(true); };
    window.addEventListener('tw:open-cookies', open);
    return () => window.removeEventListener('tw:open-cookies', open);
  }, []);

  const persist = (data) => {
    localStorage.setItem('tw_cookie_consent', JSON.stringify({ ...data, timestamp: Date.now() }));
    // Apply the analytics choice to PostHog immediately (index.html applies it
    // on subsequent page loads)
    try {
      if (window.posthog) {
        if (data.analytics === false) window.posthog.opt_out_capturing();
        else if (window.posthog.has_opted_out_capturing?.()) window.posthog.opt_in_capturing();
      }
    } catch { /* best-effort */ }
    setVisible(false);
  };

  const acceptAll = () => persist({ necessary: true, analytics: true, marketing: true });
  const acceptSelected = () => persist({ ...prefs, necessary: true });
  const declineOptional = () => persist({ necessary: true, analytics: false, marketing: false });

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      dir={dir}
      className="fixed bottom-0 left-0 right-0 z-[200] p-4 md:p-6"
      style={{ animation: 'toast-slide-up 0.5s ease forwards' }}
    >
      <div
        className="max-w-2xl mx-auto rounded-2xl p-5 md:p-6"
        style={{
          background: 'rgba(44, 24, 16, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(166, 124, 0, 0.25)',
          boxShadow: '0 -10px 40px rgba(0,0,0,0.3)',
        }}
      >
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex items-center gap-2">
            <Shield size={18} className="text-sacred-gold shrink-0" />
            <h3 className="text-cream font-body font-semibold text-sm">{t('cookies.title')}</h3>
          </div>
          <button
            onClick={declineOptional}
            className="text-cream/40 hover:text-cream/70 shrink-0"
            aria-label={t('common.close')}
            data-testid="cookie-close"
          >
            <X size={16} />
          </button>
        </div>

        <p className="text-cream/60 text-xs font-body leading-relaxed mb-4">
          {t('cookies.body')}{' '}
          <Link to="/privacy" className="text-sacred-gold underline">{t('cookies.privacyLink')}</Link>.
        </p>

        {showDetails && (
          <div className="space-y-2 mb-4" data-testid="cookie-details">
            <label className="flex items-center gap-2 text-xs text-cream/70 font-body">
              <input type="checkbox" checked disabled className="accent-sacred-gold" />
              <span className="text-cream/50">{t('cookies.necessary')}</span> — {t('cookies.necessaryDesc')}
            </label>
            <label className="flex items-center gap-2 text-xs text-cream/70 font-body cursor-pointer">
              <input
                type="checkbox"
                checked={prefs.analytics}
                onChange={e => setPrefs(p => ({ ...p, analytics: e.target.checked }))}
                className="accent-sacred-gold"
                data-testid="cookie-toggle-analytics"
              />
              <span>{t('cookies.analytics')}</span> — {t('cookies.analyticsDesc')}
            </label>
            <label className="flex items-center gap-2 text-xs text-cream/70 font-body cursor-pointer">
              <input
                type="checkbox"
                checked={prefs.marketing}
                onChange={e => setPrefs(p => ({ ...p, marketing: e.target.checked }))}
                className="accent-sacred-gold"
                data-testid="cookie-toggle-marketing"
              />
              <span>{t('cookies.marketing')}</span> — {t('cookies.marketingDesc')}
            </label>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={acceptAll}
            className="gold-button px-4 py-2 rounded-full text-xs font-body font-semibold"
            data-testid="cookie-accept-all"
          >
            {t('cookies.acceptAll')}
          </button>
          {showDetails ? (
            <button
              onClick={acceptSelected}
              className="glass-button px-4 py-2 rounded-full text-xs font-body text-cream/80 border border-cream/20"
              data-testid="cookie-save-prefs"
            >
              {t('cookies.savePrefs')}
            </button>
          ) : (
            <button
              onClick={() => setShowDetails(true)}
              className="glass-button px-4 py-2 rounded-full text-xs font-body text-cream/80 border border-cream/20"
              data-testid="cookie-customize"
            >
              {t('cookies.customize')}
            </button>
          )}
          <button
            onClick={declineOptional}
            className="px-4 py-2 rounded-full text-xs font-body text-cream/40 hover:text-cream/60"
            data-testid="cookie-decline"
          >
            {t('cookies.decline')}
          </button>
        </div>
      </div>
    </div>
  );
}
